import type { NativeEmbedParameterValues } from '@atlaskit/native-embeds-common';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';

import type { NativeEmbedChildCommand } from '../commands';
import type { NativeEmbedFeature } from '../features/main';
import type { UiConfig } from '../ui-config';
import type { URLMetaData } from '../url/types';

import type { EventCallback, ExperienceEventMap } from './experience-event-emitter';
import type { ExperienceEventEmitter } from './experience-event-emitter';

export type NativeEmbedClientId = ClientId;

/**
 * View mode for the embed container. Aligned with parent→child view-mode-change events.
 */
export type NativeEmbedViewMode = 'regular' | 'maximize';

/**
 * Branded type for experience manifest IDs. Use {@link experienceManifestId} to create.
 */
export type ExperienceManifestId = string & { __experienceManifestId: symbol };

/**
 * Converts a string to an {@link ExperienceManifestId}.
 */
export const experienceManifestId = (s: string): ExperienceManifestId => s as ExperienceManifestId;

/**
 * DTO for creating an experience: manifest plus resolved URL metadata.
 * Used by the facade (which resolves the URL once) and passed to the experience service.
 */
export type ExperienceCreateDto = {
	manifest: ExperienceManifest;
	urlMeta: URLMetaData;
};

/**
 * Manifest for registering an experience.
 */
export type ExperienceManifest = {
	/**
	 * Allows for handling child commands for the embed has sent back.
	 * This is used to extend the experience with additional functionality.
	 */
	handleChildCommands: (experience: Experience, commands: NativeEmbedChildCommand) => void;
	id: ExperienceManifestId;
	isActive: (urlMeta: URLMetaData) => boolean;
	isUrlMatch: (url: string) => boolean;
	/**
	 * Optional per-experience overrides for native embed parameter defaults.
	 */
	parameterDefaults?: Partial<NativeEmbedParameterValues>;
	/**
	 * Optional query parameters to append to the iframe URL.
	 * Applied after URL resolution and any `transformUrl` call.
	 */
	queryParams?: Record<string, string>;
	/**
	 * When true (default), the embed URL is resolved via the Linking Platform
	 * using the Smart Card component. When false, the URL is rendered directly
	 * in a basic iframe without link resolution.
	 *
	 * @default true
	 */
	resolveViaLinkingPlatform?: boolean;
	/**
	 * Controls whether URLs matching this experience should auto-convert to native embeds
	 * when pasted into the editor. When false, URLs will render as smart links by default
	 * but can still be manually upgraded to native embeds by the user.
	 *
	 * @default true
	 */
	shouldAutoConvertToNativeEmbed?: boolean;
	/**
	 * Features this experience requests during the host↔child handshake.
	 */
	supportedFeatures: Set<NativeEmbedFeature>;
	/**
	 * Optional URL transformer that allows the experience to modify the URL
	 * before it is loaded in the iframe. Can be used to add query parameters.
	 * Called after URL resolution but before the URL is passed to the iframe.
	 */
	transformUrl?: (url: string) => string;
	uiConfig: UiConfig;
};

/**
 * Host-side instance for a single native embed. One instance per embed; `id` uniquely
 * identifies it.
 *
 * Used to be passed down through the React components to specify the experience.
 *
 * Obtained by calling `getExperienceService().registerExperience(manifest)` with an
 * {@link ExperienceManifest}. Each registered embed gets its own Experience instance.
 */
export interface Experience {
	id: NativeEmbedClientId;
	/**The experience decides if it wants to handle this native embed URL based on the URL metadata */
	isActive: (urlMeta: URLMetaData) => boolean;
	/** The experience decides if it wants to handle this native embed URL based on the URL metadata */
	isUrlMatch: (urlMeta: string) => boolean;
	metaData?: URLMetaData;
	/**
	 * Subscribe to an event from this experience's child embed.
	 *
	 * Returns a synchronous unsubscribe function. Call it in your cleanup
	 * (e.g. the return value of a `useEffect`) to avoid stale listeners.
	 *
	 * Events are only emitted when the corresponding feature is agreed during
	 * the host↔child handshake. If the feature is not agreed, subscribing is
	 * harmless — the callback simply never fires.
	 *
	 * @example
	 * ```tsx
	 * useEffect(() => {
	 *     return experience.on('click-inside', () => {
	 *         // handle the click — e.g. select the editor node
	 *     });
	 * }, [experience]);
	 * ```
	 */
	on<K extends keyof ExperienceEventMap>(
		event: K,
		callback: EventCallback<ExperienceEventMap[K]>,
	): () => void;
	/**
	 * Optional query parameters to append to the iframe URL.
	 */
	queryParams?: Record<string, string>;
	/**
	 * When true (default), the embed URL is resolved via the Linking Platform
	 * in a basic iframe without link resolution.
	 *
	 * @default true
	 */
	resolveViaLinkingPlatform: boolean;
	supportedFeatures: Promise<Set<NativeEmbedFeature>>;
	/**
	 * Optional URL transformer that allows the experience to modify the URL
	 * before it is loaded in the iframe. Can be used to add query parameters.
	 */
	transformUrl?: (url: string) => string;

	uiConfig: UiConfig;
}

/**
 * Internal extension of Experience with write access to the event emitter.
 *
 * Used exclusively inside the service layer (ExperienceService). Never
 * exported from the package — consumers only see `Experience`.
 *
 * DO NOT export this type from any barrel file (index.ts).
 */
export interface InternalExperience extends Experience {
	/** @internal */
	readonly events: ExperienceEventEmitter;
}
